import { Env, Co } from '@didi/utils'
import { wechatReady } from '../core/wechat'
import { mqqReady } from '../core/mqq'
import { alipayReady } from '../core/alipay'
import { didiSDKReady } from '../core/didi'
import { getContent, isHttps, isLocalhost } from '../util/tool'
import { coordConvertToSoso } from '../util/gps'
import commonLangZh from '../lang/zh-CN/common'
import commonLangEn from '../lang/en-US/common'
import locationLangZh from '../lang/zh-CN/geo-location'
import locationLangEn from '../lang/en-US/geo-location'

const allContent = {
  'zh-CN': {
    ...commonLangZh,
    ...locationLangZh
  },
  'en-US': {
    ...commonLangEn,
    ...locationLangEn
  }
}
let lang
const domain = 'https://webapp.didistatic.com'
const fallbackUrl = domain + '/static/webapp/shield/z/sdk/sdk/2.0.1/downgrade.html'
const RES_OK = '0'
const ERR_TIMEOUT = 3
const ERR_POSITION_UNAVAILABLE = 2
const ERR_PERMISSION_DENIED = 1

const WAY_WECHAT = 'wechat'
const WAY_ALIPAY = 'alipay'
const WAY_MQQ = 'mqq'
const WAY_H5 = 'h5'
const WAY_DIDI = 'didi'

const MAPTYPE_SOSO = 'soso'
const MAPTYPE_WGS = 'wgs'
const MAPTYPE_DIDI = 'didi'

const H5_TIMEOUT = 5000
const DIDI_TIMEOUT = 10000
const MQQ_TIMEOUT = 5000

export function geoLocation (...args) {
  let options = {}
  let callback = null
  if (args.length === 1) {
    callback = args[0]
  } else {
    options = args[0]
    callback = args[1]
  }
  /* istanbul ignore else */
  if (options.lang) {
    lang = options.lang
  }
  if (options.forceH5) {
    fallback(null, options, callback)
    return
  }
  if (Env.isWechat) {
    wechatLocation(options).then((res) => {
      const converted = coordConvertToSoso({lat: res.latitude, lng: res.longitude, maptype: MAPTYPE_WGS})
      callback(null, {
        lat: converted.lat,
        lng: converted.lng,
        maptype: MAPTYPE_SOSO,
        way: WAY_WECHAT,
        accuracy: res.accuracy
      })
    }).catch((e) => {
      if (options.fallback !== false) {
        fallback(e, options, callback)
      } else {
        callback(e)
      }
    })
  } else if (Env.isMqq) {
    mqqLocation(options).then(({lat, lng}) => {
      callback(null, {
        lat,
        lng,
        maptype: MAPTYPE_SOSO,
        way: WAY_MQQ
      })
    }).catch((e) => {
      if (options.fallback !== false) {
        fallback(e, options, callback)
      } else {
        callback(e)
      }
    })
  } else if (Env.isAlipay) {
    alipayLocation(options).then((res) => {
      callback(null, {
        lat: res.latitude,
        lng: res.longitude,
        accuracy: res.accuracy,
        maptype: MAPTYPE_SOSO,
        way: WAY_ALIPAY
      })
    }).catch((e) => {
      if (options.fallback !== false) {
        fallback(e, options, callback)
      } else {
        callback(e)
      }
    })
  } else if (Env.isPassenger) {
    didiLocation(options).then(({lat, lng, city_id, area}) => {
      callback(null, {
        lat,
        lng,
        city_id,
        area,
        maptype: MAPTYPE_DIDI,
        way: WAY_DIDI
      })
    }).catch((e) => {
      if (options.fallback !== false) {
        fallback(e, options, callback)
      } else {
        callback(e)
      }
    })
  } else {
    fallback(null, options, callback)
  }
}
export default geoLocation

function fallback(error, options, callback) {
  h5Location(options).then((pos) => {
    let maptype = Env.isAlipay ? MAPTYPE_SOSO : MAPTYPE_WGS
    const lat = pos.coords.latitude
    const lng = pos.coords.longitude
    const converted = coordConvertToSoso({lat, lng, maptype})
    callback(null, {
      lat: converted.lat,
      lng: converted.lng,
      maptype: MAPTYPE_SOSO,
      way: WAY_H5,
      fallback: error
    })
  }).catch((err) => {
    let errmsg = ''
    let e = err
    if (!err.code) {
      e.code = 1
    } else {
      switch (err.code) {
        case ERR_TIMEOUT:
          errmsg = getContent(allContent, 'timeout', lang)
          break
        /* istanbul ignore next */
        case ERR_POSITION_UNAVAILABLE:
          errmsg = getContent(allContent, 'fail', lang)
          break
        /* istanbul ignore next */
        case ERR_PERMISSION_DENIED:
          errmsg = getContent(allContent, 'denied', lang)
          break
        /* istanbul ignore next */
        default:
          errmsg = getContent(allContent, 'timeout', lang)
          break
      }
      e = new Error(errmsg)
      e.code = err.code
    }
    callback(e, {
      fallback: error
    })
  })
}

function timeout(way, delay = 3000) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      let err = new Error(way + ' ' + getContent(allContent, 'timeout', lang))
      err.code = 3
      reject(err)
    }, delay)
  })
}

function wechatLocation(options) {
  const delayTime = options.wechatDelay || options.delay
  function location() {
    return new Promise((resolve, reject) => {
      wechatReady((err, wx) => {
        if (!err) {
          wx.getLocation({
            success(res) {
              if (res.errMsg === 'getLocation:ok') {
                resolve(res)
              } else {
                reject(new Error(WAY_WECHAT + ' ' + getContent(allContent, 'fail', lang)))
              }
            },
            fail() {
              reject(new Error(WAY_WECHAT + ' ' + getContent(allContent, 'fail', lang)))
            }
          })
        } else {
          reject(new Error(getContent(allContent, 'wechatBridgeError', lang)))
        }
      })
    })
  }

  return Promise.race([location(), timeout(WAY_WECHAT, delayTime)])
}

function mqqLocation(options) {
  const delayTime = options.wechatDelay || options.delay || MQQ_TIMEOUT
  function location() {
    return new Promise((resolve, reject) => {
      mqqReady((err, mqq) => {
        if (!err) {
          mqq.sensor.getLocation((ret, lat, lng) => {
            if (ret === 0) {
              resolve({
                lat,
                lng
              })
            } else {
              reject(new Error(WAY_MQQ + ' ' + getContent(allContent, 'fail', lang)))
            }
          })
        } else {
          reject(new Error(getContent(allContent, 'mqqBridgeError', lang)))
        }
      })
    })
  }

  return Promise.race([location(), timeout(WAY_MQQ, delayTime)])
}

function alipayLocation(options) {
  const delayTime = options.wechatDelay || options.delay
  function location() {
    return new Promise((resolve, reject) => {
      alipayReady((err, bridge) => {
        if (!err) {
          bridge.call('getLocation', {
            requestType: 2
          }, (res) => {
            if (!res.error) {
              resolve(res)
            } else {
              reject(new Error(WAY_ALIPAY + ' ' + getContent(allContent, 'fail', lang)))
            }
          })
        }
      })
    })
  }

  return Promise.race([location(), timeout(WAY_ALIPAY, delayTime)])
}

function didiLocation(options) {
  const delayTime = options.didiDelay || options.delay || DIDI_TIMEOUT
  function location() {
    return new Promise((resolve, reject) => {
      didiSDKReady((err, bridge) => {
        if (!err) {
          bridge.call('getLocationInfo', {}, function(err, res) {
            if (`${err.errno}` === RES_OK) {
              resolve(res)
            } else {
              reject(new Error(WAY_DIDI + ' ' + getContent(allContent, 'fail', lang)))
            }
          })
        }
      })
    })
  }

  return Promise.race([location(), timeout(WAY_DIDI, delayTime)])
}

function h5Location(options) {
  const delayTime = options.h5Delay || options.delay || H5_TIMEOUT
  function location() {
    return new Promise((resolve, reject) => {
      if (isHttps() || isLocalhost()) {
        try {
          const navigatorOptions = {
            enableHighAccuracy: true,
            timeout: delayTime,
            maximumAge: 2000
          }
          navigator.geolocation.getCurrentPosition((pos) => {
            const lat = pos.coords.latitude
            const lng = pos.coords.longitude
            if (lat && lng) {
              resolve(pos)
            } else {
              reject(new Error(getContent(allContent, 'fail', lang)))
            }
          }, (err) => {
            reject(err)
          }, navigatorOptions)
        } catch (e) {
          reject(e)
        }
      } else {
        Co.init(domain)
        Co.send({
          domain: domain,
          url: fallbackUrl,
          param: {
            type: 'geo'
          },
          call: 'geoCallback',
          callback: (res) => {
            if (res.err) {
              reject(res.err)
              return
            }
            resolve(res.result)
          }
        })
      }
    })
  }

  return Promise.race([location(), timeout(WAY_H5, delayTime)])
}
